import {
  type ChangeEvent,
  type KeyboardEvent,
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";

import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import TextField, { type TextFieldProps } from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import debounce from "lodash/debounce";

import TeamPill from "components/team-pill";
import useLatestRef from "hooks/use-latest-ref.hook";
import { useLazyGetMentionCandidatesQuery } from "services/mentions";
import {
  type MentionCandidate,
  isTeamCandidate
} from "services/types/mentions.type";

import { markMentionsUsed } from "./launch/mentions-launch";
import {
  type MentionPick,
  candidateMatchesQuery,
  getActiveMentionQuery,
  insertMention
} from "./mention-text.util";
import { describeCandidate } from "./use-mention-editor.hook";

const MAX_RESULTS = 8;
const SEARCH_DEBOUNCE_MS = 200;

type MentionTextFieldProps = {
  value: string;
  onChange: (value: string) => void;
  /** The people picked so far; send `getMentionedIdsInText(value, mentions)` on submit. */
  mentions: MentionPick[];
  onMentionsChange: (mentions: MentionPick[]) => void;
  /** Off: a plain text field (no picker, no candidate search). */
  enabled: boolean;
  /** People at this clinic are listed first. */
  clinicId?: number;
  /** TEAMS_FEATURE: list the practice's teams before the people. Off, the request is as before. */
  includeTeams?: boolean;
  /** Runs for keys the picker does not use (e.g. Ctrl+Enter to submit). */
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
} & Omit<TextFieldProps, "value" | "onChange" | "onKeyDown">;

const LIST_MAX_HEIGHT = 320;

/**
 * A plain-text field (multiline by default) with an "@" teammate picker — for task comments.
 *
 * Type "@" at the start of a word and a list of teammates opens UNDER the field, filtered by
 * prefix as you type ("@kar"). ↑ ↓ move, Enter or a click picks, Esc closes. Picking replaces
 * "@kar" with "@Karen Ruiz " and records the pick.
 *
 * The list is a plain absolutely positioned Paper, deliberately not a MUI Popper/Autocomplete:
 * under the app theme a Popper crashes jsdom (`:has()` selector, see root CLAUDE.md), and a
 * comment box needs nothing a Popper adds.
 */
export default function MentionTextField({
  value,
  onChange,
  mentions,
  onMentionsChange,
  enabled,
  clinicId,
  includeTeams = false,
  onKeyDown,
  multiline = true,
  ...textFieldProps
}: MentionTextFieldProps) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [fetchCandidates] = useLazyGetMentionCandidatesQuery();
  const fetchRef = useLatestRef(fetchCandidates);
  const clinicIdRef = useLatestRef(clinicId);
  const includeTeamsRef = useLatestRef(includeTeams);

  const [active, setActive] = useState<{ start: number; query: string } | null>(
    null
  );
  const [options, setOptions] = useState<MentionCandidate[]>([]);
  const [highlighted, setHighlighted] = useState(0);
  const requestSeqRef = useRef(0);
  const pendingCaretRef = useRef<number | null>(null);

  const search = useMemo(
    () =>
      debounce(async (query: string) => {
        const seq = ++requestSeqRef.current;
        try {
          const candidates = await fetchRef
            .current({
              search: query,
              clinic: clinicIdRef.current,
              limit: MAX_RESULTS,
              ...(includeTeamsRef.current ? { include_teams: true } : {})
            })
            .unwrap();
          if (seq !== requestSeqRef.current) return;
          setOptions(candidates);
          setHighlighted(0);
        } catch {
          // A failed search shows no list rather than an error mid-sentence.
          if (seq === requestSeqRef.current) setOptions([]);
        }
      }, SEARCH_DEBOUNCE_MS),
    // Stable refs: the debounced search is created once.
    [clinicIdRef, fetchRef, includeTeamsRef]
  );
  useEffect(() => () => search.cancel(), [search]);

  const close = useCallback(() => {
    search.cancel();
    requestSeqRef.current += 1;
    setActive(null);
    setOptions([]);
  }, [search]);

  const updateQuery = useCallback(
    (text: string, caret: number | null | undefined) => {
      if (!enabled) return;
      const next = getActiveMentionQuery(text, caret);
      if (!next) {
        if (active) close();
        return;
      }
      // onSelect also fires after every keystroke; the same "@kar" needs no second search.
      if (
        active &&
        active.start === next.start &&
        active.query === next.query
      ) {
        return;
      }
      setActive(next);
      search(next.query);
    },
    [active, close, enabled, search]
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange(event.target.value);
      updateQuery(event.target.value, event.target.selectionStart);
    },
    [onChange, updateQuery]
  );

  const pick = useCallback(
    (candidate: MentionCandidate) => {
      if (!active) return;
      const expectedCaret = active.start + active.query.length + 1;
      const caret = inputRef.current?.selectionStart ?? expectedCaret;
      // The caret left the "@kar" being typed (arrow keys, Home/End): inserting now would delete or
      // repeat text, so close the list instead.
      if (caret !== expectedCaret) {
        close();
        return;
      }
      const next = insertMention(value, active.start, caret, candidate.name);
      pendingCaretRef.current = next.caret;
      onChange(next.text);
      const kind = isTeamCandidate(candidate) ? "team" : "user";
      if (
        !mentions.some(
          (mention) =>
            mention.id === candidate.id && (mention.kind ?? "user") === kind
        )
      ) {
        onMentionsChange([
          ...mentions,
          // A person keeps the shape it always had; only a team carries `kind`.
          kind === "team"
            ? { id: candidate.id, name: candidate.name, kind }
            : { id: candidate.id, name: candidate.name }
        ]);
      }
      close();
      markMentionsUsed();
    },
    [active, close, mentions, onChange, onMentionsChange, value]
  );

  // Put the caret after the inserted name once the new value has rendered.
  useEffect(() => {
    const caret = pendingCaretRef.current;
    if (caret == null || !inputRef.current) return;
    pendingCaretRef.current = null;
    inputRef.current.focus();
    inputRef.current.setSelectionRange(caret, caret);
  }, [value]);

  // Results from an earlier, shorter query stay on screen until the new search answers; show only
  // the ones that still match what is typed, so Enter can never pick a stale name.
  const visibleOptions = useMemo(
    () =>
      active
        ? options.filter((candidate) =>
            candidateMatchesQuery(candidate.name, active.query)
          )
        : [],
    [active, options]
  );
  const isOpen = Boolean(active) && visibleOptions.length > 0;
  const highlightedIndex =
    visibleOptions.length > 0
      ? Math.min(highlighted, visibleOptions.length - 1)
      : 0;

  // The comments panel scrolls; bring the list into view when it opens.
  useEffect(() => {
    if (isOpen) listRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (isOpen) {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          setHighlighted(
            (index) =>
              (Math.min(index, visibleOptions.length - 1) + 1) %
              visibleOptions.length
          );
          return;
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          setHighlighted(
            (index) =>
              (Math.min(index, visibleOptions.length - 1) -
                1 +
                visibleOptions.length) %
              visibleOptions.length
          );
          return;
        }
        if (
          (event.key === "Enter" || event.key === "Tab") &&
          // Enter that commits an IME composition (Chinese, Japanese, Korean input) is not a pick.
          !event.nativeEvent.isComposing &&
          !event.ctrlKey &&
          !event.metaKey &&
          !event.shiftKey
        ) {
          event.preventDefault();
          const candidate = visibleOptions[highlightedIndex];
          if (candidate) pick(candidate);
          return;
        }
        if (event.key === "Escape") {
          // Close the list, not the dialog the field sits in.
          event.preventDefault();
          event.stopPropagation();
          close();
          return;
        }
      }
      onKeyDown?.(event);
    },
    [close, highlightedIndex, isOpen, onKeyDown, pick, visibleOptions]
  );

  const listboxId = textFieldProps.id
    ? `${textFieldProps.id}-mentions`
    : "mention-text-field-listbox";

  // Keep the keyboard-highlighted name visible in a list taller than its box.
  useEffect(() => {
    if (!isOpen) return;
    document
      .getElementById(`${listboxId}-${highlightedIndex}`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [highlightedIndex, isOpen, listboxId]);

  // Open upward when there is no room below, e.g. a comment box near the bottom of the patient
  // header's hover preview, which cannot scroll to reveal a list hanging past the viewport.
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [openUpward, setOpenUpward] = useState(false);
  // Measured against the list's real height (one name is short), again as the matches change.
  const optionCount = visibleOptions.length;
  useLayoutEffect(() => {
    if (!isOpen || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const listHeight = Math.min(
      listRef.current?.offsetHeight || LIST_MAX_HEIGHT,
      LIST_MAX_HEIGHT
    );
    const spaceBelow = window.innerHeight - rect.bottom;
    setOpenUpward(spaceBelow < listHeight + 8 && rect.top > spaceBelow);
  }, [isOpen, optionCount]);

  if (!enabled) {
    // Flag off: exactly the plain field the caller rendered before mentions (no wrapper, no
    // caret/blur handlers, no extra renders).
    return (
      <TextField
        {...textFieldProps}
        multiline={multiline}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={onKeyDown}
      />
    );
  }

  return (
    <Box ref={wrapperRef} sx={{ position: "relative", width: "100%" }}>
      <TextField
        {...textFieldProps}
        multiline={multiline}
        value={value}
        inputRef={inputRef}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={(event) => {
          const target = event.target as HTMLTextAreaElement;
          updateQuery(target.value ?? value, target.selectionStart);
        }}
        // Arrow keys, Home and End move the caret without a change event: re-read the "@kar".
        onSelect={(event) => {
          const target = event.target as HTMLTextAreaElement;
          if (target.selectionStart !== target.selectionEnd) {
            if (active) close();
            return;
          }
          updateQuery(target.value ?? value, target.selectionStart);
        }}
        onBlur={(event) => {
          close();
          textFieldProps.onBlur?.(event);
        }}
        inputProps={{
          ...textFieldProps.inputProps,
          ...(enabled
            ? {
                role: "combobox",
                "aria-autocomplete": "list",
                "aria-expanded": isOpen,
                "aria-controls": isOpen ? listboxId : undefined,
                "aria-activedescendant": isOpen
                  ? `${listboxId}-${highlightedIndex}`
                  : undefined
              }
            : {})
        }}
      />
      {isOpen && (
        <MentionCandidateList
          ref={listRef}
          id={listboxId}
          options={visibleOptions}
          highlightedIndex={highlightedIndex}
          openUpward={openUpward}
          onHighlight={setHighlighted}
          onPick={pick}
        />
      )}
    </Box>
  );
}

type MentionCandidateListProps = {
  id: string;
  options: MentionCandidate[];
  highlightedIndex: number;
  openUpward: boolean;
  onHighlight: (index: number) => void;
  onPick: (candidate: MentionCandidate) => void;
};

/** The open @ list: one row per teammate, name over role and clinic. */
const MentionCandidateList = forwardRef<
  HTMLDivElement,
  MentionCandidateListProps
>(function MentionCandidateList(
  { id, options, highlightedIndex, openUpward, onHighlight, onPick },
  ref
) {
  return (
    <Paper
      ref={ref}
      id={id}
      role="listbox"
      aria-label="Mention a teammate"
      elevation={8}
      // Keep focus (and the caret) in the field while clicking an option.
      onMouseDown={(event) => event.preventDefault()}
      sx={{
        position: "absolute",
        ...(openUpward
          ? { bottom: "100%", mb: 0.5 }
          : { top: "100%", mt: 0.5 }),
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.modal + 1,
        maxHeight: LIST_MAX_HEIGHT,
        overflowY: "auto",
        py: 0.5
      }}
    >
      {options.map((candidate, index) => {
        const subtitle = describeCandidate(candidate);
        const selected = index === highlightedIndex;
        return (
          <Box
            key={`${isTeamCandidate(candidate) ? "team" : "user"}-${candidate.id}`}
            id={`${id}-${index}`}
            role="option"
            aria-selected={selected}
            onMouseEnter={() => onHighlight(index)}
            onClick={() => onPick(candidate)}
            sx={{
              px: 2,
              py: 1,
              cursor: "pointer",
              backgroundColor: selected ? "action.selected" : undefined
            }}
          >
            {isTeamCandidate(candidate) ? (
              <TeamPill
                name={candidate.name}
                color={candidate.color}
                testId={`mention-team-option-${candidate.id}`}
              />
            ) : (
              <Typography variant="body1" fontWeight={600}>
                {candidate.name}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        );
      })}
    </Paper>
  );
});
